import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner, Form } from 'react-bootstrap';
import { Book, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import type { KnowledgeBase } from '../../../types/knowledge-base';

interface KnowledgeBaseSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (selectedIds: string[], selectedKBs: KnowledgeBase[]) => void;
    selectedKnowledgeBases: string[];
}

const KnowledgeBaseSelectionModal: React.FC<KnowledgeBaseSelectionModalProps> = ({
                                                                                     isOpen,
                                                                                     onClose,
                                                                                     onSelect,
                                                                                     selectedKnowledgeBases
                                                                                 }) => {
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set(selectedKnowledgeBases));
    const [searchQuery, setSearchQuery] = useState('');

    // Reset selected state when modal opens and when selectedKnowledgeBases changes
    useEffect(() => {
        setSelected(new Set(selectedKnowledgeBases));
    }, [isOpen, selectedKnowledgeBases]);

    useEffect(() => {
        if (isOpen) {
            fetchKnowledgeBases();
        }
    }, [isOpen]);

    const fetchKnowledgeBases = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Add all=true to get all KBs without pagination
            const data = await apiService.get(`${API_ENDPOINTS.INDEXES.LIST}?all=true`);
            setKnowledgeBases(Array.isArray(data) ? data : []);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load knowledge bases';
            setError(errorMessage);
            console.error('Error fetching knowledge bases:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleSelect = (id: string) => {
        const newSelected = new Set(selected);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelected(newSelected);
    };

    const handleConfirm = () => {
        const selectedIds = Array.from(selected);
        const selectedKBs = knowledgeBases.filter(kb => selected.has(kb.id));
        onSelect(selectedIds, selectedKBs);
        onClose();
    };

    const filteredKnowledgeBases = knowledgeBases.filter(kb =>
        kb.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        kb.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const selectedKBs = knowledgeBases.filter(kb => selected.has(kb.id));

    return (
        <Modal show={isOpen} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Select Knowledge Bases</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">{error}</Alert>
                )}

                <Form.Control
                    type="text"
                    placeholder="Search knowledge bases..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mb-3"
                />

                {/* Selected Knowledge Bases Section */}
                {selectedKBs.length > 0 && (
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">
                                Selected Knowledge Bases ({selectedKBs.length})
                            </small>
                        </div>
                        <div className="border rounded">
                            <div className="p-2">
                                {selectedKBs.map(kb => (
                                    <div
                                        key={kb.id}
                                        className="d-flex align-items-center justify-content-between py-1"
                                    >
                                        <div className="d-flex align-items-center overflow-hidden">
                                            <Book size={16} className="text-primary me-2 flex-shrink-0" />
                                            <div className="flex-grow-1">
                                                <div className="text-truncate">
                                                    <span className="fw-medium">{kb.name}</span>
                                                </div>
                                                {kb.description && (
                                                    <small className="text-muted d-block text-truncate">{kb.description}</small>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="link"
                                            className="p-0 ms-2 flex-shrink-0"
                                            onClick={() => handleToggleSelect(kb.id)}
                                            title="Remove from selection"
                                        >
                                            <X size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Available Knowledge Bases List */}
                <div className="border rounded" style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff'
                }}>
                    {filteredKnowledgeBases.map(kb => {
                        const isReady = kb.status === 'ready';
                        const isSelected = selected.has(kb.id);

                        return (
                            <div
                                key={kb.id}
                                className={`
                                    d-flex align-items-center p-3 border-bottom
                                    ${isReady ? 'hover:bg-light' : 'opacity-50'}
                                `}
                            >
                                <Form.Check
                                    type="checkbox"
                                    id={`kb-${kb.id}`}
                                    checked={isSelected}
                                    onChange={() => isReady && handleToggleSelect(kb.id)}
                                    disabled={!isReady}
                                    className="me-3"
                                />
                                <Book size={16} className="me-2 text-primary" />
                                <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-medium">{kb.name}</span>
                                        <span className={`badge ${
                                            kb.status === 'ready' ? 'bg-success-subtle text-success-emphasis' :
                                                kb.status === 'processing' ? 'bg-warning-subtle text-warning-emphasis' :
                                                    'bg-danger-subtle text-danger-emphasis'
                                        }`}>
                                            {kb.status.charAt(0).toUpperCase() + kb.status.slice(1)}
                                        </span>
                                    </div>
                                    {kb.description && (
                                        <small className="text-muted d-block mt-1">{kb.description}</small>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {isLoading && (
                        <div className="text-center p-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading knowledge bases...
                        </div>
                    )}

                    {!isLoading && filteredKnowledgeBases.length === 0 && (
                        <div className="text-center p-3 text-muted">
                            {searchQuery ? 'No matching knowledge bases found' : 'No knowledge bases found'}
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={isLoading}
                >
                    Confirm Selection ({selected.size})
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default KnowledgeBaseSelectionModal;